import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api/client";
import type { WooCommerceIntegrationPayload } from "@/lib/api/types";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, PackageCheck, Plug, RefreshCw, ShoppingCart, Sparkles, Truck, X, XCircle } from "lucide-react";
import * as React from "react";

const APP_BASE_URL = typeof window === "undefined" ? "" : window.location.origin;

type PendingWooCommerceConnect = {
  siteUrl: string;
  returnUrl: string;
  state: string;
  pluginVersion: string | null;
};

type TestRateFeedback = {
  id: number;
  kind: "success" | "error";
  title: string;
  message: string;
  detail?: string;
};

function readPendingWooCommerceConnect(): PendingWooCommerceConnect | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  if (params.get("woocommerce_connect") !== "1") return null;

  const siteUrl = params.get("site_url")?.trim();
  const returnUrl = params.get("return_url")?.trim();
  const state = params.get("state")?.trim();
  if (!siteUrl || !returnUrl || !state) return null;

  return {
    siteUrl,
    returnUrl,
    state,
    pluginVersion: params.get("plugin_version")?.trim() || null,
  };
}

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
      ok
        ? "border-emerald-500/30 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
        : "border-amber-500/30 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
    }`}>
      {ok ? <CheckCircle2 className="size-3" /> : <AlertTriangle className="size-3" />}
      {label}
    </span>
  );
}

function ChecklistRow({ done, label, detail }: { done: boolean; label: string; detail: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
      {done ? <CheckCircle2 className="mt-0.5 size-4 text-emerald-600" /> : <XCircle className="mt-0.5 size-4 text-muted-foreground" />}
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}

function FieldSummary({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}

function TestRateFeedbackToast({ feedback, onDismiss }: { feedback: TestRateFeedback; onDismiss: () => void }) {
  const isSuccess = feedback.kind === "success";
  const colors = ["#34d399", "#60a5fa", "#fbbf24", "#f472b6", "#a78bfa"];

  return (
    <div
      aria-live="polite"
      className={`relative overflow-hidden rounded-xl border px-3.5 py-3 shadow-sm transition-[transform,opacity,border-color,background-color] duration-200 ${
        isSuccess
          ? "border-emerald-300/70 bg-emerald-50 text-emerald-950 dark:border-emerald-500/30 dark:bg-emerald-950/30 dark:text-emerald-100"
          : "border-red-300/70 bg-red-50 text-red-950 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-100"
      }`}
    >
      <style>{`
        @keyframes shippeek-test-rate-confetti {
          0% { opacity: 0; transform: translate3d(0, 4px, 0) rotate(0deg) scale(0.6); }
          18% { opacity: 1; }
          100% { opacity: 0; transform: translate3d(var(--x), var(--y), 0) rotate(var(--r)) scale(1); }
        }
      `}</style>
      {isSuccess ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 overflow-visible" aria-hidden="true">
          {Array.from({ length: 18 }).map((_, index) => (
            <span
              key={`${feedback.id}-${index}`}
              className="absolute top-5 size-1.5 rounded-[2px]"
              style={{
                left: `${8 + index * 5}%`,
                backgroundColor: colors[index % colors.length],
                animation: "shippeek-test-rate-confetti 900ms cubic-bezier(0.2, 0, 0, 1) both",
                animationDelay: `${index * 28}ms`,
                ["--x" as string]: `${(index % 2 === 0 ? -1 : 1) * (14 + (index % 5) * 7)}px`,
                ["--y" as string]: `${-28 - (index % 4) * 9}px`,
                ["--r" as string]: `${(index % 2 === 0 ? -1 : 1) * (90 + index * 11)}deg`,
              }}
            />
          ))}
        </div>
      ) : null}

      <div className="relative flex items-start gap-3">
        <div className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${isSuccess ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200" : "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-200"}`}>
          {isSuccess ? <Sparkles className="size-4" /> : <AlertTriangle className="size-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{feedback.title}</p>
          <p className={`mt-0.5 text-xs leading-5 ${isSuccess ? "text-emerald-800 dark:text-emerald-200/90" : "text-red-800 dark:text-red-200/90"}`}>{feedback.message}</p>
          {feedback.detail ? <p className="mt-1 text-[11px] font-medium tabular-nums opacity-75">{feedback.detail}</p> : null}
        </div>
        <button
          type="button"
          aria-label="Dismiss test rate message"
          className="-mr-2 -mt-2 flex size-10 shrink-0 items-center justify-center rounded-full text-current opacity-60 transition-[opacity,transform] duration-150 hover:opacity-100 active:scale-[0.96]"
          onClick={onDismiss}
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

function buildManualAuthorizeUrl(siteUrl: string, returnUrl: string) {
  const state = crypto.randomUUID();
  const url = new URL("/api/integrations/woocommerce/authorize", APP_BASE_URL || "https://app.shippeek.com");
  url.searchParams.set("site_url", siteUrl);
  url.searchParams.set("return_url", returnUrl);
  url.searchParams.set("state", state);
  url.searchParams.set("plugin_version", "manual-preview");
  return url.toString();
}

function WooCommerceControlCenter() {
  const [status, setStatus] = React.useState<WooCommerceIntegrationPayload | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);
  const [testFeedback, setTestFeedback] = React.useState<TestRateFeedback | null>(null);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [pendingConnect, setPendingConnect] = React.useState<PendingWooCommerceConnect | null>(() => readPendingWooCommerceConnect());
  const [previewSiteUrl, setPreviewSiteUrl] = React.useState("https://store.example.com");
  const [previewReturnUrl, setPreviewReturnUrl] = React.useState("https://store.example.com/wp-admin/admin.php?page=shippeek-live-checkout-rates");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await apiClient.getWooCommerceIntegration());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load WooCommerce integration.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const runTestRate = async () => {
    setActionLoading("test");
    setActionMessage(null);
    setError(null);
    setTestFeedback(null);
    try {
      const result = await apiClient.testWooCommerceRate();
      setTestFeedback({
        id: Date.now(),
        kind: "success",
        title: "Test rate passed",
        message: `${result.rateCount} ${result.mode.toUpperCase()} ${result.rateCount === 1 ? "rate" : "rates"} returned. WooCommerce checkout is ready to show Shippeek rates.`,
        detail: `${result.responseTimeMs}ms response time`,
      });
      await load();
    } catch (err) {
      setTestFeedback({
        id: Date.now(),
        kind: "error",
        title: "Test rate needs attention",
        message: err instanceof Error ? err.message : "Test rate failed.",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect WooCommerce in Shippeek? The WordPress site may still store your primary API key; rotate the key to fully revoke access.")) return;
    setActionLoading("disconnect");
    setActionMessage(null);
    setError(null);
    setTestFeedback(null);
    try {
      const result = await apiClient.disconnectWooCommerce();
      setActionMessage(result.warning);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to disconnect WooCommerce.");
    } finally {
      setActionLoading(null);
    }
  };

  const approveConnection = async () => {
    if (!pendingConnect) return;
    setActionLoading("approve");
    setActionMessage(null);
    setError(null);
    setTestFeedback(null);
    try {
      const result = await apiClient.approveWooCommerceConnection(pendingConnect);
      window.location.assign(result.redirectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to approve WooCommerce connection.");
      setActionLoading(null);
    }
  };

  const cancelConnection = () => {
    setPendingConnect(null);
    window.history.replaceState(null, "", "/account/integrations");
  };

  if (loading && !status) {
    return (
      <Card>
        <CardContent className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" /> Loading WooCommerce integration…
        </CardContent>
      </Card>
    );
  }

  const checklist = status?.checklist;
  const settings = status?.settings;
  const authorizeUrl = buildManualAuthorizeUrl(previewSiteUrl, previewReturnUrl);

  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}
      {actionMessage && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
          {actionMessage}
        </div>
      )}

      {pendingConnect && (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Plug className="size-4" /> Approve WooCommerce connection
            </CardTitle>
            <CardDescription>
              WordPress is asking Shippeek to connect this store. Approve only if you recognize the site.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <FieldSummary label="Store" value={<a className="underline underline-offset-4" href={pendingConnect.siteUrl} target="_blank" rel="noreferrer">{pendingConnect.siteUrl}</a>} />
              <FieldSummary label="Return URL" value={<span className="break-all text-xs">{pendingConnect.returnUrl}</span>} />
              <FieldSummary label="Plugin version" value={pendingConnect.pluginVersion ?? "Unknown"} />
            </div>
            <div className="rounded-lg border border-amber-400/40 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
              Approving sends this WordPress site a short-lived code that can be exchanged for your primary Shippeek API key. Disconnecting later does not revoke the key; rotate it if the site is compromised.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void approveConnection()} disabled={actionLoading === "approve"}>
                {actionLoading === "approve" ? <Loader2 className="mr-2 size-4 animate-spin" /> : <CheckCircle2 className="mr-2 size-4" />}
                Approve and return to WordPress
              </Button>
              <Button variant="outline" onClick={cancelConnection} disabled={actionLoading === "approve"}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex size-11 items-center justify-center rounded-xl border border-border bg-muted/50">
                <ShoppingCart className="size-5" />
              </div>
              <div>
                <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                  Shippeek Live Checkout Rates
                  <StatusBadge ok={Boolean(status?.connected)} label={status?.connected ? "Connected" : "Not connected"} />
                </CardTitle>
                <CardDescription className="mt-1">
                  Live LTL and parcel carrier-account rates for WooCommerce checkout.
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
                <RefreshCw className={`mr-1.5 size-3 ${loading ? "animate-spin" : ""}`} />Refresh
              </Button>
              {status?.connected && (
                <Button variant="outline" size="sm" onClick={() => void disconnect()} disabled={actionLoading === "disconnect"}>
                  {actionLoading === "disconnect" ? <Loader2 className="mr-1.5 size-3 animate-spin" /> : null}
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-3 md:grid-cols-3">
            <FieldSummary label="Connected store" value={status?.siteUrl ? <a className="underline underline-offset-4" href={status.siteUrl} target="_blank" rel="noreferrer">{status.siteUrl}</a> : "Not connected"} />
            <FieldSummary label="Plugin version" value={status?.pluginVersion ?? "Waiting for plugin"} />
            <FieldSummary label="First successful rate" value={status?.firstSuccessfulRateAt ? new Date(status.firstSuccessfulRateAt).toLocaleString() : "Not yet"} />
          </div>

          {status?.connected ? (
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
              <p className="font-medium">Ship origin from WooCommerce</p>
              {status.storeOrigin ? (
                <p className="mt-1 text-muted-foreground">
                  {status.storeOrigin.address}, {status.storeOrigin.city}, {status.storeOrigin.state} {status.storeOrigin.postalCode}
                  {status.storeOrigin.addressBookId ? (
                    <> — saved to <a className="underline underline-offset-4" href="/addresses">Address Book</a></>
                  ) : null}
                </p>
              ) : (
                <p className="mt-1 text-muted-foreground">Reconnect the plugin to import your WooCommerce store address.</p>
              )}
              {status.effectiveOrigin && status.effectiveOrigin.source === "shippeek_demo_lane" ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Checkout rates use the Texas demo origin ({status.effectiveOrigin.address}, {status.effectiveOrigin.city}, {status.effectiveOrigin.state} {status.effectiveOrigin.postalCode}) until your store ZIP is in the demo lane.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="rounded-lg border border-amber-400/40 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
            <p className="font-semibold">Primary API key is used for v1</p>
            <p className="mt-1 text-xs leading-5">
              The plugin receives the account's primary Shippeek API key after browser connect. Disconnecting the integration updates Shippeek status, but full revocation requires rotating the primary API key.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><PackageCheck className="size-4" />Activation checklist</CardTitle>
            <CardDescription>Live checkout stays blocked until the setup test passes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ChecklistRow done={Boolean(checklist?.storeConnected)} label="Store connected" detail="WooCommerce has completed the browser callback connect flow." />
            <ChecklistRow done={Boolean(checklist?.activeCarrier)} label="Active carrier credentials" detail="At least one Shippeek carrier account is active for live rating." />
            <ChecklistRow done={Boolean(checklist?.originConfigured)} label="Origin/defaults configured" detail="Ship-from origin and safe product defaults are owned in Shippeek." />
            <ChecklistRow done={Boolean(checklist?.testRatePassed)} label="Successful test rate" detail="A controlled Shippeek test rate returned usable live rates." />
            <ChecklistRow done={Boolean(checklist?.liveCheckoutEnabled)} label="Live checkout enabled" detail="WooCommerce can show Shippeek rates to shoppers." />
            {status?.connected && !checklist?.activeCarrier && (
              <div className="rounded-lg border border-amber-400/40 bg-amber-50 px-3 py-2.5 text-xs leading-5 text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
                No active carrier credentials are connected yet. Add or activate a carrier before running the WooCommerce test rate.
                <a className="ml-1 font-medium underline underline-offset-4" href="/carriers">Open carriers</a>
              </div>
            )}
            {testFeedback ? (
              <TestRateFeedbackToast feedback={testFeedback} onDismiss={() => setTestFeedback(null)} />
            ) : null}
            <Button className="w-full active:scale-[0.96] transition-transform" onClick={() => void runTestRate()} disabled={!status?.connected || !checklist?.activeCarrier || actionLoading === "test"}>
              {actionLoading === "test" ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Truck className="mr-2 size-4" />}
              {status?.connected && !checklist?.activeCarrier ? "Connect carrier to test rates" : "Run test rate"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Plug className="size-4" />Plugin connect preview</CardTitle>
            <CardDescription>The WordPress plugin builds this URL and opens Shippeek login.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="woo-site-url">WooCommerce site URL</Label>
                <Input id="woo-site-url" value={previewSiteUrl} onChange={(event) => setPreviewSiteUrl(event.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="woo-return-url">WordPress return URL</Label>
                <Input id="woo-return-url" value={previewReturnUrl} onChange={(event) => setPreviewReturnUrl(event.target.value)} />
              </div>
            </div>
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs break-all text-muted-foreground">
              {authorizeUrl}
            </div>
            <Button variant="outline" size="sm" asChild>
              <a href={authorizeUrl} target="_blank" rel="noreferrer">
                Open connect URL <ExternalLink className="ml-1.5 size-3" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Shippeek-owned WooCommerce settings</CardTitle>
          <CardDescription>Synced to the plugin; shoppers see final shipping prices only.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <FieldSummary label="Checkout timeout" value={`${settings?.timeout.checkoutSeconds ?? 8}s (${settings?.timeout.minSeconds ?? 3}-${settings?.timeout.maxSeconds ?? 15}s)`} />
          <FieldSummary label="Accessorials" value={settings?.accessorials.enabled ? "Enabled by default" : "Disabled"} />
          <FieldSummary label="Safe defaults" value={`${settings?.defaults.parcelWeightLbs ?? 10} lb, ${settings?.defaults.parcelLengthIn ?? 12}×${settings?.defaults.parcelWidthIn ?? 12}×${settings?.defaults.parcelHeightIn ?? 12} in`} />
          <FieldSummary label="Rate display" value={settings?.display.mode === "all_rates" ? "Shopper selects rate" : settings?.display.mode ?? "all_rates"} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent WooCommerce rate requests</CardTitle>
          <CardDescription>Used for activation, diagnostics, and marketplace-readiness evidence.</CardDescription>
        </CardHeader>
        <CardContent>
          {status?.recentRequests?.length ? (
            <div className="divide-y divide-border rounded-lg border border-border">
              {status.recentRequests.map((request) => (
                <div key={request.id} className="grid gap-2 px-3 py-3 text-sm sm:grid-cols-[1fr_auto_auto_auto] sm:items-center">
                  <div>
                    <p className="font-medium">{request.mode?.toUpperCase() ?? "RATE"} · {request.status}</p>
                    <p className="text-xs text-muted-foreground">{request.createdAt ? new Date(request.createdAt).toLocaleString() : "Unknown time"}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{request.rateCount} rates</span>
                  <span className="text-xs text-muted-foreground">{request.responseTimeMs ?? "—"}ms</span>
                  <span className="text-xs text-red-600">{request.errorMessage}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              No WooCommerce rate requests yet.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function IntegrationsPage() {
  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Plug className="size-5" />
          Integrations
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect storefronts and operational systems to Shippeek. WooCommerce is the first checkout integration being productized.
        </p>
      </div>

      <WooCommerceControlCenter />
    </div>
  );
}
